import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getDashboardAll } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Badge } from '../components/ui/badge';
import { 
  Microphone, 
  Calendar, 
  CheckCircle, 
  Clock, 
  ArrowRight,
  TrendUp,
  Users,
  X
} from '@phosphor-icons/react';
import { format, isToday, isPast, parseISO } from 'date-fns';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DashboardPage() {
  const { user, setUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [dealStats, setDealStats] = useState(null);
  const [recentTasks, setRecentTasks] = useState([]);
  const [recentMeetings, setRecentMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWelcome, setShowWelcome] = useState(false);

  useEffect(() => {
    fetchDashboardData();
    if (user?.is_new_user) {
      setShowWelcome(true);
    }
  }, [user]);

  const fetchDashboardData = async () => {
    try {
      const response = await getDashboardAll();
      const data = response.data;
      setStats(data.stats);
      setDealStats(data.deal_stats);
      setRecentTasks(data.recent_tasks);
      setRecentMeetings(data.recent_meetings);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDueDate = (dateStr) => {
    const date = typeof dateStr === 'string' ? parseISO(dateStr) : dateStr;
    if (isToday(date)) return 'Today';
    if (isPast(date)) return 'Overdue';
    return format(date, 'MMM d');
  };

  const dismissWelcome = async () => {
    setShowWelcome(false);
    try {
      await axios.post(`${API_URL}/api/auth/dismiss-welcome`, {}, { withCredentials: true });
      if (user) setUser({ ...user, is_new_user: false });
    } catch (e) {
      console.error('Failed to dismiss welcome:', e);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-8 h-8 border-2 border-[#007AFF] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard">
      {/* Welcome Message for New Users */}
      {showWelcome && (
        <div className="bg-gradient-to-r from-[#007AFF] to-[#5856D6] rounded-2xl p-6 text-white relative" data-testid="welcome-banner">
          <button
            onClick={dismissWelcome}
            className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors"
            data-testid="dismiss-welcome-btn"
          >
            <X size={20} />
          </button>
          <h2 className="text-xl sm:text-2xl font-bold mb-2">Welcome to Zelvrix!</h2>
          <p className="text-white/80 mb-4">Start by recording your first voice note.</p>
          <Link
            to="/meetings/voice"
            className="inline-flex items-center gap-2 bg-white text-[#007AFF] font-semibold rounded-full px-5 py-2.5 text-sm hover:bg-white/90 transition-colors"
            data-testid="welcome-record-btn"
          >
            <Microphone size={18} weight="bold" />
            Record Now
          </Link>
        </div>
      )}

      {/* Hero Section - Record CTA */}
      <div className="card-glass p-8 text-center">
        <p className="text-[#86868B] text-sm font-medium mb-3">Quick Action</p>
        <h1 className="text-2xl sm:text-3xl font-bold text-[#1D1D1F] mb-4">
          Record a Meeting
        </h1>
        <p className="text-[#86868B] mb-8 max-w-md mx-auto">
          Tap to start recording. AI will transcribe and create follow-ups automatically.
        </p>
        
        {/* Big Record Button */}
        <Link 
          to="/meetings/voice" 
          className="record-btn-large mx-auto mb-6 block w-fit"
          data-testid="main-record-btn"
        >
          <Microphone size={48} weight="bold" className="text-white" />
        </Link>
        
        <p className="text-[#86868B] text-sm">or</p>
        
        <Link 
          to="/meetings/new" 
          className="btn-ghost inline-flex items-center gap-2 mt-2"
          data-testid="manual-entry-btn"
        >
          Type manually <ArrowRight size={16} />
        </Link>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card-apple p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-[#007AFF]/10 flex items-center justify-center">
              <Users size={20} className="text-[#007AFF]" />
            </div>
          </div>
          <p className="text-2xl font-bold text-[#1D1D1F]">{stats?.total_meetings || 0}</p>
          <p className="text-sm text-[#86868B]">Meetings</p>
        </div>

        <div className="card-apple p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-[#FF9500]/10 flex items-center justify-center">
              <Clock size={20} className="text-[#FF9500]" />
            </div>
          </div>
          <p className="text-2xl font-bold text-[#1D1D1F]">{stats?.pending_tasks || 0}</p>
          <p className="text-sm text-[#86868B]">Pending Tasks</p>
        </div>

        <div className="card-apple p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-[#34C759]/10 flex items-center justify-center">
              <TrendUp size={20} className="text-[#34C759]" />
            </div>
          </div>
          <p className="text-2xl font-bold text-[#1D1D1F]">${dealStats?.total_value?.toLocaleString() || 0}</p>
          <p className="text-sm text-[#86868B]">Pipeline</p>
        </div>

        <div className="card-apple p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-[#5856D6]/10 flex items-center justify-center">
              <CheckCircle size={20} className="text-[#5856D6]" />
            </div>
          </div>
          <p className="text-2xl font-bold text-[#1D1D1F]">{dealStats?.win_rate || 0}%</p>
          <p className="text-sm text-[#86868B]">Win Rate</p>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Upcoming Tasks */}
        <div className="card-apple p-4 sm:p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-semibold text-[#1D1D1F]">Upcoming Tasks</h2>
            <Link to="/tasks" className="text-[#007AFF] text-sm font-medium shrink-0 ml-2">
              See all
            </Link>
          </div>
          
          {recentTasks.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle size={40} className="mx-auto mb-3 text-[#C7C7CC]" />
              <p className="text-[#86868B]">No pending tasks</p>
            </div>
          ) : (
            <div className="space-y-3">
              {recentTasks.slice(0, 4).map((task) => (
                <div 
                  key={task.id} 
                  className="flex items-center gap-3 p-3 rounded-xl bg-[#F5F5F7] hover:bg-[#E8E8ED] transition-colors min-w-0"
                  data-testid={`task-item-${task.id}`}
                >
                  <div className={`w-3 h-3 rounded-full shrink-0 ${
                    task.priority === 'high' ? 'bg-[#FF3B30]' : 
                    task.priority === 'medium' ? 'bg-[#FF9500]' : 'bg-[#34C759]'
                  }`} />
                  <p className="flex-1 min-w-0 font-medium text-[#1D1D1F] text-sm truncate">{task.title}</p>
                  <span className={`text-xs font-medium shrink-0 px-2 py-1 rounded-lg ${
                    task.due_date && isPast(parseISO(task.due_date)) && !isToday(parseISO(task.due_date))
                      ? 'bg-[#FF3B30]/10 text-[#FF3B30]'
                      : task.due_date && isToday(parseISO(task.due_date))
                        ? 'bg-[#FF9500]/10 text-[#FF9500]'
                        : 'bg-[#007AFF]/10 text-[#007AFF]'
                  }`} data-testid={`task-due-${task.id}`}>
                    {formatDueDate(task.due_date)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Meetings */}
        <div className="card-apple p-4 sm:p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-semibold text-[#1D1D1F]">Recent Meetings</h2>
            <Link to="/meetings" className="text-[#007AFF] text-sm font-medium shrink-0 ml-2">
              See all
            </Link>
          </div>
          
          {recentMeetings.length === 0 ? (
            <div className="text-center py-8">
              <Calendar size={40} className="mx-auto mb-3 text-[#C7C7CC]" />
              <p className="text-[#86868B]">No meetings yet</p>
              <Link to="/meetings/voice" className="text-[#007AFF] text-sm font-medium mt-2 inline-block">
                Record your first meeting
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {recentMeetings.slice(0, 4).map((meeting) => (
                <Link 
                  key={meeting.id} 
                  to={`/meetings/${meeting.id}`}
                  className="flex items-center gap-3 p-3 rounded-xl bg-[#F5F5F7] hover:bg-[#E8E8ED] transition-colors min-w-0"
                  data-testid={`meeting-item-${meeting.id}`}
                >
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#007AFF] to-[#5856D6] flex items-center justify-center text-white font-semibold text-sm shrink-0">
                    {meeting.client_name.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-[#1D1D1F] truncate">{meeting.client_name}</p>
                    <p className="text-sm text-[#86868B] truncate">
                      {meeting.client_company || format(parseISO(meeting.meeting_date), 'MMM d')}
                    </p>
                  </div>
                  {meeting.deal_value && (
                    <span className="text-[#34C759] font-semibold text-sm shrink-0">
                      ${meeting.deal_value.toLocaleString()}
                    </span>
                  )}
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Deal Status Overview */}
      {dealStats && dealStats.total_deals > 0 && (
        <div className="card-apple p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-semibold text-[#1D1D1F]">Deal Pipeline</h2>
            <Link to="/pipeline" className="text-[#007AFF] text-sm font-medium">
              View Kanban
            </Link>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="text-center p-4 rounded-xl bg-[#007AFF]/5">
              <p className="text-2xl font-bold text-[#007AFF]">{dealStats.by_status.open.count}</p>
              <p className="text-xs text-[#86868B] mt-1">Open</p>
            </div>
            <div className="text-center p-4 rounded-xl bg-[#FF9500]/5">
              <p className="text-2xl font-bold text-[#FF9500]">{dealStats.by_status.pending.count}</p>
              <p className="text-xs text-[#86868B] mt-1">Pending</p>
            </div>
            <div className="text-center p-4 rounded-xl bg-[#34C759]/5">
              <p className="text-2xl font-bold text-[#34C759]">{dealStats.by_status.closed_won.count}</p>
              <p className="text-xs text-[#86868B] mt-1">Won</p>
            </div>
            <div className="text-center p-4 rounded-xl bg-[#FF3B30]/5">
              <p className="text-2xl font-bold text-[#FF3B30]">{dealStats.by_status.closed_lost.count}</p>
              <p className="text-xs text-[#86868B] mt-1">Lost</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
